import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Trash2 } from "lucide-react";

// ── Simple diff algorithm ────────────────────────────────────

interface DiffLine {
    type: "add" | "remove" | "same";
    text: string;
    lineA?: number;
    lineB?: number;
}

function computeDiff(a: string, b: string): DiffLine[] {
    const linesA = a.split("\n");
    const linesB = b.split("\n");

    // Simple LCS-based diff
    const m = linesA.length;
    const n = linesB.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () =>
        Array(n + 1).fill(0),
    );

    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (linesA[i - 1] === linesB[j - 1]) {
                dp[i]![j] = dp[i - 1]![j - 1]! + 1;
            } else {
                dp[i]![j] = Math.max(dp[i - 1]![j]!, dp[i]![j - 1]!);
            }
        }
    }

    // Backtrack
    const result: DiffLine[] = [];
    let i = m, j = n;
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && linesA[i - 1] === linesB[j - 1]) {
            result.unshift({ type: "same", text: linesA[i - 1]!, lineA: i, lineB: j });
            i--; j--;
        } else if (j > 0 && (i === 0 || dp[i]![j - 1]! >= dp[i - 1]![j]!)) {
            result.unshift({ type: "add", text: linesB[j - 1]!, lineB: j });
            j--;
        } else {
            result.unshift({ type: "remove", text: linesA[i - 1]!, lineA: i });
            i--;
        }
    }

    return result;
}

// ── Component ────────────────────────────────────────────────

export function DiffTool() {
    const [textA, setTextA] = useState("");
    const [textB, setTextB] = useState("");
    const [copied, setCopied] = useState(false);
    const [viewMode, setViewMode] = useState<"unified" | "side">("unified");

    useEffect(() => {
        document.title = "Diff Checker — Compare Text Online — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute("content",
                "Free online diff checker. Compare two texts side-by-side or as a unified diff. Syntax highlighting for additions and deletions.");
        }
    }, []);

    const diff = useMemo(() => {
        if (!textA && !textB) return [];
        return computeDiff(textA, textB);
    }, [textA, textB]);

    const stats = useMemo(() => {
        const added = diff.filter((d) => d.type === "add").length;
        const removed = diff.filter((d) => d.type === "remove").length;
        const same = diff.filter((d) => d.type === "same").length;
        return { added, removed, same };
    }, [diff]);

    const copyDiff = async () => {
        const text = diff
            .map((d) => {
                const prefix = d.type === "add" ? "+ " : d.type === "remove" ? "- " : "  ";
                return prefix + d.text;
            })
            .join("\n");
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">ballah code</Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">Diff Checker</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">Diff Checker</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Compare two texts to see what changed. Useful for comparing code, configs, or AI outputs.
                    </p>
                </div>

                {/* Input panels */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    <div className="flex flex-col">
                        <div className="px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Original</span>
                        </div>
                        <textarea
                            value={textA}
                            onChange={(e) => setTextA(e.target.value)}
                            placeholder="Paste original text here..."
                            spellCheck={false}
                            className="w-full min-h-[250px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                        />
                    </div>
                    <div className="flex flex-col">
                        <div className="px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Modified</span>
                        </div>
                        <textarea
                            value={textB}
                            onChange={(e) => setTextB(e.target.value)}
                            placeholder="Paste modified text here..."
                            spellCheck={false}
                            className="w-full min-h-[250px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                        />
                    </div>
                </div>

                {/* Controls */}
                {diff.length > 0 && (
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <div className="flex rounded-lg border border-white/[0.06] overflow-hidden">
                            {(["unified", "side"] as const).map((m) => (
                                <button
                                    key={m}
                                    onClick={() => setViewMode(m)}
                                    className={`px-3.5 py-2 text-[12px] transition-colors ${viewMode === m ? "bg-white/[0.08] text-zinc-200" : "text-zinc-600 hover:text-zinc-400"}`}
                                >
                                    {m === "unified" ? "Unified" : "Side by side"}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center gap-4 text-[12px] text-zinc-600">
                            <span className="text-green-400">+{stats.added}</span>
                            <span className="text-red-400">−{stats.removed}</span>
                            <span>{stats.same} unchanged</span>
                        </div>

                        <div className="flex items-center gap-2 ml-auto">
                            <button onClick={copyDiff} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors">
                                {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                                Copy diff
                            </button>
                            <button onClick={() => { setTextA(""); setTextB(""); }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-600 hover:text-zinc-400 transition-colors">
                                <Trash2 size={12} /> Clear
                            </button>
                        </div>
                    </div>
                )}

                {/* Diff output */}
                {diff.length > 0 && (
                    <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden mb-16 overflow-x-auto">
                        {diff.map((d, i) => (
                            <div
                                key={i}
                                className={`flex text-[13px] font-mono ${d.type === "add"
                                    ? "bg-green-400/[0.04]"
                                    : d.type === "remove"
                                        ? "bg-red-400/[0.04]"
                                        : ""
                                    } ${i < diff.length - 1 ? "border-b border-white/[0.02]" : ""}`}
                            >
                                <span className="w-12 text-right pr-3 py-1.5 text-zinc-700 text-[11px] select-none shrink-0 border-r border-white/[0.04]">
                                    {d.lineA || ""}
                                </span>
                                <span className="w-12 text-right pr-3 py-1.5 text-zinc-700 text-[11px] select-none shrink-0 border-r border-white/[0.04]">
                                    {d.lineB || ""}
                                </span>
                                <span
                                    className={`w-6 text-center py-1.5 text-[11px] select-none shrink-0 ${d.type === "add"
                                        ? "text-green-400"
                                        : d.type === "remove"
                                            ? "text-red-400"
                                            : "text-zinc-800"
                                        }`}
                                >
                                    {d.type === "add" ? "+" : d.type === "remove" ? "−" : " "}
                                </span>
                                <span
                                    className={`flex-1 py-1.5 pr-4 whitespace-pre overflow-hidden ${d.type === "add"
                                        ? "text-green-300"
                                        : d.type === "remove"
                                            ? "text-red-300"
                                            : "text-zinc-500"
                                        }`}
                                >
                                    {d.text || " "}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">About text diffing</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>A diff compares two texts line by line and shows what was added, removed, or left unchanged. It's the same concept behind Git diffs and code reviews. This tool uses an LCS (Longest Common Subsequence) algorithm for accurate results.</p>
                        <p>Common use cases include comparing AI-generated outputs, reviewing config file changes, and checking code modifications before committing.</p>
                        <p>Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/regex-tester" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Regex Tester</Link>
                        <Link to="/tools/json-formatter" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JSON Formatter</Link>
                        <Link to="/tools/hash-generator" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Hash Generator</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
